import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import Header from '../../components/ui/Header';
import Image from '../../components/AppImage';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { blogService } from '../../services/blogService';
import 'highlight.js/styles/github-dark.css';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const data = await blogService?.getBlogById(id);
      setBlog(data);
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError(err?.message || 'Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading blog post...</p>
          </div>
        </div>
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="text-center max-w-md">
            <Icon name="AlertCircle" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Blog Post Not Found</h2>
            <p className="text-muted-foreground mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
            <Link to="/blog-posts">
              <Button variant="default" iconName="ArrowLeft" iconPosition="left">
                Back to Blog Posts
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog?.title} - Portfolio Blog</title>
        <meta name="description" content={blog?.excerpt} />
      </Helmet>
      <Header />
      <article className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="relative h-96 md:h-[500px] lg:h-[600px] overflow-hidden">
          <Image
            src={blog?.image}
            alt={blog?.imageAlt}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 lg:p-12">
            <div className="max-w-4xl mx-auto">
              <span className="inline-block px-3 py-1 bg-accent text-accent-foreground rounded-md text-sm font-medium mb-4">
                {blog?.category}
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {blog?.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/90">
                <div className="flex items-center gap-2">
                  {blog?.author?.avatarUrl && (
                    <Image
                      src={blog?.author?.avatarUrl}
                      alt={blog?.author?.fullName}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  )}
                  <span className="font-medium">{blog?.author?.fullName}</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Icon name="Calendar" size={16} color="white" />
                  <span>{formatDate(blog?.publishedDate)}</span>
                </div>
                <span>•</span>
                <div className="flex items-center gap-1">
                  <Icon name="Clock" size={16} color="white" />
                  <span>{blog?.readTime} min read</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              {blog?.excerpt}
            </p>
            <div className="prose prose-lg max-w-none prose-headings:text-foreground prose-p:text-foreground prose-strong:text-foreground prose-a:text-accent prose-code:text-accent prose-pre:bg-slate-900 prose-pre:text-slate-100">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeHighlight]}
              >
                {blog?.content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-12 pt-8 border-t border-border flex flex-wrap items-center justify-between gap-4">
            <Link to="/blog-posts">
              <Button variant="outline" iconName="ArrowLeft" iconPosition="left">
                Back to All Posts
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" iconName="Share2" iconPosition="left">
                Share
              </Button>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

export default BlogDetail;
